import {
  ZadLocalizedText,
  ZadOfferDiscountType,
  ZadOfferTargetType,
  ZadOfferWindowStatus,
  ZadPromoCodeManagement,
  ZadPromoUsageMode,
  ZadPromotionManagement,
} from '../../../models/zad'
import {formatZadTnd} from './productHelpers'
import {resolveZadUserMessage, ZadApiError} from './errors'

export const ZAD_DISCOUNT_TYPES: ZadOfferDiscountType[] = ['PERCENTAGE', 'FIXED_AMOUNT']
export const ZAD_TARGET_TYPES: ZadOfferTargetType[] = ['ALL', 'CATEGORY', 'PRODUCT_TYPE', 'PRODUCTS']
export const ZAD_USAGE_MODES: ZadPromoUsageMode[] = [
  'UNLIMITED',
  'TOTAL_LIMIT',
  'ONCE_PER_CUSTOMER',
  'LIMIT_PER_CUSTOMER',
]
export const ZAD_CUSTOMER_USAGE_MODES: ZadPromoUsageMode[] = [
  'ONCE_PER_CUSTOMER',
  'LIMIT_PER_CUSTOMER',
]

export function normalizeZadOfferCode(value: unknown): string {
  return String(value || '')
    .trim()
    .toUpperCase()
}

export function emptyLocalizedText(): ZadLocalizedText {
  return {fr: '', en: '', ar: ''}
}

export function sanitizeLocalizedText(value: unknown): ZadLocalizedText {
  if (typeof value === 'string') {
    return {fr: value.trim(), en: '', ar: ''}
  }
  const raw = value && typeof value === 'object' ? (value as Record<string, unknown>) : {}
  return {
    fr: String(raw.fr || '').trim(),
    en: String(raw.en || '').trim(),
    ar: String(raw.ar || '').trim(),
  }
}

export function pickLocalizedText(value: ZadLocalizedText | undefined, locale: string): string {
  const text = value || emptyLocalizedText()
  const lang = String(locale || 'fr').slice(0, 2)
  return text[lang as keyof ZadLocalizedText] || text.fr || text.en || text.ar || ''
}

export function localizedHasContent(value: ZadLocalizedText): boolean {
  return Boolean(value.fr || value.en || value.ar)
}

function asId(value: unknown): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'object' && value !== null && '_id' in value) {
    return String((value as {_id?: unknown})._id || '')
  }
  return String(value)
}

function asIdList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(asId).filter(Boolean) : []
}

function asWindowStatus(value: unknown): ZadOfferWindowStatus {
  if (value === 'ACTIVE' || value === 'UPCOMING' || value === 'EXPIRED' || value === 'SUSPENDED') {
    return value
  }
  return 'SUSPENDED'
}

function asDiscountType(value: unknown): ZadOfferDiscountType {
  return value === 'FIXED_AMOUNT' ? 'FIXED_AMOUNT' : 'PERCENTAGE'
}

function asTargetType(value: unknown): ZadOfferTargetType {
  if (value === 'CATEGORY' || value === 'PRODUCT_TYPE' || value === 'PRODUCTS' || value === 'ALL') {
    return value
  }
  return 'ALL'
}

function asUsageMode(value: unknown): ZadPromoUsageMode {
  if (
    value === 'TOTAL_LIMIT' ||
    value === 'ONCE_PER_CUSTOMER' ||
    value === 'LIMIT_PER_CUSTOMER' ||
    value === 'UNLIMITED'
  ) {
    return value
  }
  return 'UNLIMITED'
}

export function sanitizePromotion(value: unknown): ZadPromotionManagement | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const id = asId(raw._id || raw.id)
  const code = normalizeZadOfferCode(raw.code)
  if (!id || !code) return null
  const startAt = raw.startAt ? String(raw.startAt) : ''
  if (!startAt) return null
  return {
    _id: id,
    code,
    title: sanitizeLocalizedText(raw.title),
    description: sanitizeLocalizedText(raw.description),
    discountType: asDiscountType(raw.discountType),
    discountValue: Number(raw.discountValue) || 0,
    startAt,
    endAt: raw.endAt ? String(raw.endAt) : null,
    isActive: raw.isActive === true,
    isPublic: raw.isPublic === true,
    isFeatured: raw.isFeatured === true,
    targetType: asTargetType(raw.targetType),
    categories: asIdList(raw.categories),
    productTypes: asIdList(raw.productTypes),
    products: asIdList(raw.products),
    minimumAmount: Number(raw.minimumAmount) || 0,
    priority: Number.isFinite(Number(raw.priority)) ? Number(raw.priority) : 0,
    bannerImageUrl: raw.bannerImageUrl ? String(raw.bannerImageUrl) : null,
    status: asWindowStatus(raw.status),
    createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
  }
}

export function sanitizePromoCode(value: unknown): ZadPromoCodeManagement | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const id = asId(raw._id || raw.id)
  const code = normalizeZadOfferCode(raw.code)
  if (!id || !code) return null
  const startAt = raw.startAt ? String(raw.startAt) : ''
  if (!startAt) return null
  const usageMode = asUsageMode(raw.usageMode)
  return {
    _id: id,
    code,
    title: sanitizeLocalizedText(raw.title),
    description: sanitizeLocalizedText(raw.description),
    discountType: asDiscountType(raw.discountType),
    discountValue: Number(raw.discountValue) || 0,
    usageMode,
    startAt,
    endAt: raw.endAt ? String(raw.endAt) : null,
    isActive: raw.isActive === true,
    isPublic: raw.isPublic === true,
    maxUses: raw.maxUses == null || raw.maxUses === '' ? null : Number(raw.maxUses),
    usageCount: Number(raw.usageCount) || 0,
    maxUsesPerCustomer:
      raw.maxUsesPerCustomer == null || raw.maxUsesPerCustomer === ''
        ? null
        : Number(raw.maxUsesPerCustomer),
    minimumAmount: Number(raw.minimumAmount) || 0,
    targetType: asTargetType(raw.targetType),
    categories: asIdList(raw.categories),
    productTypes: asIdList(raw.productTypes),
    products: asIdList(raw.products),
    status: asWindowStatus(raw.status),
    createdAt: raw.createdAt ? String(raw.createdAt) : undefined,
    updatedAt: raw.updatedAt ? String(raw.updatedAt) : undefined,
  }
}

export function extractPromotionList(data: unknown): {
  items: ZadPromotionManagement[]
  total: number
  totalPages: number
} {
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const source = raw.promotions
  const items = Array.isArray(source)
    ? source.map(sanitizePromotion).filter((item): item is ZadPromotionManagement => Boolean(item))
    : []
  return {
    items,
    total: typeof raw.total === 'number' ? raw.total : items.length,
    totalPages: typeof raw.totalPages === 'number' ? raw.totalPages : 1,
  }
}

export function extractPromotionItem(data: unknown): ZadPromotionManagement | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  return sanitizePromotion(raw.promotion) || sanitizePromotion(raw)
}

export function extractPromoCodeList(data: unknown): {
  items: ZadPromoCodeManagement[]
  total: number
  totalPages: number
} {
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const source = raw.promoCodes
  const items = Array.isArray(source)
    ? source.map(sanitizePromoCode).filter((item): item is ZadPromoCodeManagement => Boolean(item))
    : []
  return {
    items,
    total: typeof raw.total === 'number' ? raw.total : items.length,
    totalPages: typeof raw.totalPages === 'number' ? raw.totalPages : 1,
  }
}

export function extractPromoCodeItem(data: unknown): ZadPromoCodeManagement | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  return sanitizePromoCode(raw.promoCode) || sanitizePromoCode(raw)
}

export function extractGeneratedPromoCode(data: unknown): string {
  if (!data || typeof data !== 'object') return ''
  const raw = data as Record<string, unknown>
  return normalizeZadOfferCode(raw.code)
}

export function formatOfferDiscount(type: ZadOfferDiscountType, value: number): string {
  if (type === 'PERCENTAGE') return `-${value} %`
  return `-${formatZadTnd(value)}`
}

export function offerStatusLabelId(status: ZadOfferWindowStatus): string {
  if (status === 'ACTIVE') return 'ZAD.OFFERS.STATUS_ACTIVE'
  if (status === 'UPCOMING') return 'ZAD.OFFERS.STATUS_UPCOMING'
  if (status === 'EXPIRED') return 'ZAD.OFFERS.STATUS_EXPIRED'
  return 'ZAD.OFFERS.STATUS_SUSPENDED'
}

export function offerStatusBadgeClass(status: ZadOfferWindowStatus): string {
  if (status === 'ACTIVE') return 'badge-light-success'
  if (status === 'UPCOMING') return 'badge-light-info'
  if (status === 'EXPIRED') return 'badge-light-secondary'
  return 'badge-light-warning'
}

export function targetTypeLabelId(type: ZadOfferTargetType): string {
  if (type === 'CATEGORY') return 'ZAD.OFFERS.TARGET_CATEGORY'
  if (type === 'PRODUCT_TYPE') return 'ZAD.OFFERS.TARGET_PRODUCT_TYPE'
  if (type === 'PRODUCTS') return 'ZAD.OFFERS.TARGET_PRODUCTS'
  return 'ZAD.OFFERS.TARGET_ALL'
}

export function usageModeLabelId(mode: ZadPromoUsageMode): string {
  if (mode === 'TOTAL_LIMIT') return 'ZAD.PROMO_CODES.USAGE_TOTAL_LIMIT'
  if (mode === 'ONCE_PER_CUSTOMER') return 'ZAD.PROMO_CODES.USAGE_ONCE_CUSTOMER'
  if (mode === 'LIMIT_PER_CUSTOMER') return 'ZAD.PROMO_CODES.USAGE_LIMIT_CUSTOMER'
  return 'ZAD.PROMO_CODES.USAGE_UNLIMITED'
}

export function isCustomerUsageMode(mode: ZadPromoUsageMode): boolean {
  return ZAD_CUSTOMER_USAGE_MODES.includes(mode)
}

export function isPromoCodeLimitReached(item: ZadPromoCodeManagement): boolean {
  return (
    item.usageMode === 'TOTAL_LIMIT' &&
    typeof item.maxUses === 'number' &&
    item.maxUses >= 1 &&
    item.usageCount >= item.maxUses
  )
}

export function formatPromoUsage(item: ZadPromoCodeManagement): string {
  if (item.usageMode === 'TOTAL_LIMIT' && typeof item.maxUses === 'number') {
    return `${item.usageCount} / ${item.maxUses}`
  }
  return String(item.usageCount)
}

export function formatOfferDateTime(value?: string | null, locale = 'fr'): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date)
}

export function toDateTimeLocalValue(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`
}

export function fromDateTimeLocalValue(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString()
}

export function targetIdsForType(
  targetType: ZadOfferTargetType,
  ids: string[]
): {categories: string[]; productTypes: string[]; products: string[]} {
  return {
    categories: targetType === 'CATEGORY' ? ids : [],
    productTypes: targetType === 'PRODUCT_TYPE' ? ids : [],
    products: targetType === 'PRODUCTS' ? ids : [],
  }
}

export function idsForTarget(item: {
  targetType: ZadOfferTargetType
  categories: string[]
  productTypes: string[]
  products: string[]
}): string[] {
  if (item.targetType === 'CATEGORY') return item.categories
  if (item.targetType === 'PRODUCT_TYPE') return item.productTypes
  if (item.targetType === 'PRODUCTS') return item.products
  return []
}

export function buildPromotionWritePayload(values: {
  code: string
  title: ZadLocalizedText
  description: ZadLocalizedText
  discountType: ZadOfferDiscountType
  discountValue: number
  startAt: string
  endAt: string
  targetType: ZadOfferTargetType
  targetIds: string[]
  minimumAmount: number
  priority: number
  isPublic: boolean
  isFeatured: boolean
}) {
  const targets = targetIdsForType(values.targetType, values.targetIds)
  const payload: Record<string, unknown> = {
    code: normalizeZadOfferCode(values.code),
    title: values.title,
    discountType: values.discountType,
    discountValue: values.discountType === 'PERCENTAGE' ? Number(values.discountValue) : Number(values.discountValue),
    startAt: fromDateTimeLocalValue(values.startAt),
    targetType: values.targetType,
    ...targets,
    minimumAmount: Number(values.minimumAmount) || 0,
    priority: Number.isInteger(Number(values.priority)) ? Number(values.priority) : 0,
    isPublic: values.isPublic === true,
    isFeatured: values.isFeatured === true,
  }
  payload.endAt = values.endAt ? fromDateTimeLocalValue(values.endAt) : null
  if (localizedHasContent(values.description)) payload.description = values.description
  return payload
}

export function buildPromoCodeWritePayload(values: {
  code: string
  title: ZadLocalizedText
  description: ZadLocalizedText
  discountType: ZadOfferDiscountType
  discountValue: number
  startAt: string
  endAt: string
  targetType: ZadOfferTargetType
  targetIds: string[]
  minimumAmount: number
  usageMode: ZadPromoUsageMode
  maxUses: number | ''
  maxUsesPerCustomer: number | ''
  isPublic: boolean
}) {
  const targets = targetIdsForType(values.targetType, values.targetIds)
  const payload: Record<string, unknown> = {
    code: normalizeZadOfferCode(values.code),
    title: values.title,
    discountType: values.discountType,
    discountValue: Number(values.discountValue),
    startAt: fromDateTimeLocalValue(values.startAt),
    usageMode: values.usageMode,
    targetType: values.targetType,
    ...targets,
    minimumAmount: Number(values.minimumAmount) || 0,
    isPublic: values.isPublic === true,
  }
  payload.endAt = values.endAt ? fromDateTimeLocalValue(values.endAt) : null
  if (localizedHasContent(values.description)) payload.description = values.description
  if (values.usageMode === 'TOTAL_LIMIT') payload.maxUses = Number(values.maxUses)
  if (values.usageMode === 'LIMIT_PER_CUSTOMER') {
    payload.maxUsesPerCustomer = Number(values.maxUsesPerCustomer)
  }
  return payload
}

export async function copyZadText(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value)
      return true
    }
  } catch {
    /* fallback below */
  }
  try {
    const input = document.createElement('textarea')
    input.value = value
    input.setAttribute('readonly', 'true')
    document.body.appendChild(input)
    input.select()
    const ok = document.execCommand('copy')
    document.body.removeChild(input)
    return ok
  } catch {
    return false
  }
}

export function resolveZadOfferMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    DUPLICATE_PROMOTION_CODE: 'ZAD.PROMOTIONS.DUPLICATE_CODE',
    DUPLICATE_PROMO_CODE: 'ZAD.PROMO_CODES.DUPLICATE_CODE',
    PROMOTION_NOT_FOUND: 'ZAD.PROMOTIONS.NOT_FOUND',
    PROMO_CODE_NOT_FOUND: 'ZAD.PROMO_CODES.NOT_FOUND',
    INVALID_DISCOUNT: 'ZAD.OFFERS.INVALID_DISCOUNT',
    INVALID_TARGET: 'ZAD.OFFERS.INVALID_TARGET',
    INVALID_PROMOTION_WINDOW: 'ZAD.OFFERS.INVALID_WINDOW',
    INVALID_USAGE_MODE: 'ZAD.PROMO_CODES.INVALID_USAGE',
    INVALID_PROMO_PREFIX: 'ZAD.PROMO_CODES.INVALID_PREFIX',
    PROMO_CODE_GENERATION_FAILED: 'ZAD.PROMO_CODES.GENERATE_FAILED',
    CATEGORY_NOT_FOUND: 'ZAD.PRODUCTS.CATEGORY_NOT_FOUND',
    PRODUCT_TYPE_NOT_FOUND: 'ZAD.PRODUCTS.TYPE_NOT_FOUND',
    PRODUCT_NOT_FOUND: 'ZAD.PRODUCTS.NOT_FOUND',
    VALIDATION_ERROR: 'ZAD.OFFERS.VALIDATION_ERROR',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}
